import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as customerSupportApi from '../../apis/customer-support-api';
import ICustomerData, {
  ICustomerAudit,
} from "../../models/customer-data.interface";
import { RootState } from '../store';
import { addBookingCases, bookingReducers } from './customer-bookings';
import { initialCustomerState } from './customer-state';
import { resetErrorDialog, setErrorDialog } from './notification-slice';
import SliceError from '../../utils/SliceError';

import * as CustomerTrips from './customer-trips';
import { addTripsCases, tripsReducers } from './customer-trips';
import {IComment} from "../../models/comments-data.interface";

export type SearchType = 'EMAIL' | 'ID' | 'DRIVER' | 'BOOKING' | 'PAYMENT';

export const fetchCustomerDataByIdentifier = createAsyncThunk<
  ICustomerData,
  boolean | undefined,
  { state: RootState }
>('customer/fetchCustomerDataByIdentifier', async (_, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const identifier = state.customer.search.identifier?.value;
  let customer = null;

  dispatch(resetErrorDialog());

  if (!identifier) {
    dispatch(setErrorDialog({
      code: 'MISSING_CUSTOMER_SEARCH_FIELD',
      title: 'Not Valid',
      message: 'Please enter customer Email or ID.',
    }));
  }

  try {
    if (state.customer.search.type === 'ID') {
      customer = await customerSupportApi.getCustomerById(identifier);
    } else if (state.customer.search.type === 'EMAIL' || state.customer.search.type === 'DRIVER') {
      const response = await customerSupportApi.getCustomerByEmailOrDriverId(identifier, state.customer.search.type)

      if (response?.length > 1) {
        return response;
      } else if (response?.length === 1) {
        customer = await customerSupportApi.getCustomerById(response[0]?.id);
      }
    }

    if (!customer) {
      throw new SliceError({
        code: state.customer.search.type === 'ID' ? 'ID_NOT_FOUND' : (state.customer.search.type === 'DRIVER' ? 'DRIVER_ID_NOT_FOUND' : 'EMAIL_NOT_FOUND'),
        title: 'Not Found',
        message:
          state.customer.search.type === 'ID' ? 'Customer not found with this ID.' :
            (state.customer.search.type === 'DRIVER' ? 'Customer not found with this Driver ID.' :
              'Customer not found with this Email.'),
      })
    }

    customer.idVerificationSessions = await customerSupportApi.getCustomerIdVerificationSessions(customer.id);
    customer.comments = await customerSupportApi.getCustomerComments(customer.id);

    return customer;
  } catch (error) {

    let message = error.message;
    let code = error.code;

    if (error.message === 'FORBIDDEN' || error.message === 'Failed to fetch') {
      message = 'Access forbidden.';
      code = 'FORBIDDEN_CUSTOMER';
    }

    dispatch(setErrorDialog({
      message: message,
      code: code || 'ERROR_CODE',
      title: error.title || 'Error Notification ',
    }));

    return rejectWithValue(error.message);
  }
},
  {
    condition: (refresh, { getState }) => {
      // execute the function if it's explicitly requested
      if (refresh) {
        return true;
      }

      const { customer } = getState();
      return customer.status === 'idle'; // execute the fetch function only when the status is idle
    },
  }
);

export const fetchCustomerDataById = createAsyncThunk<
  ICustomerData,
  { id: string },
  { state: RootState }
>('customer/fetchCustomerDataById', async ({ id }, { dispatch, rejectWithValue }) => {
  dispatch(resetErrorDialog());

  if (!id) {
    throw new SliceError({
      code: 'MISSING_CUSTOMER_SEARCH_FIELD',
      title: 'Not Valid',
      message: 'Please enter customer Email or ID.',
    })
  }

  try {
    const customer = await customerSupportApi.getCustomerById(id);
    if (!customer || !customer.id) {
      throw new SliceError({
        code: 'USER_NOT_FOUND',
        title: 'Not found error',
        message: 'User not found'
      });
    }

    const verificationSessions = await customerSupportApi.getCustomerIdVerificationSessions(id);
    customer.idVerificationSessions = verificationSessions?.sort((a, b) => new Date(b.startDateTime).getTime() - new Date(a.startDateTime).getTime());

    customer.comments = await customerSupportApi.getCustomerComments(customer.id);

    return customer;
  } catch (error) {
    dispatch(setErrorDialog({
      message: error.message,
      code: error.code || 'ERROR_CODE',
      title: error.title || 'Error Notification ',
    }));

    return rejectWithValue(error.message);
  }

},
  {
    condition: (refresh, { getState }) => {
      // execute the function if it's explicitly requested
      if (refresh) {
        return true;
      }

      const { customer } = getState();
      return customer.status === 'idle'; // execute the fetch function only when the status is idle
    },
  }
);

export const fetchCustomerAuditTrail = createAsyncThunk<
  ICustomerAudit,
  { id: string },
  { state: RootState }
>('customer/fetchCustomerAuditTrail', async ({ id }, { dispatch, rejectWithValue }) => {
  const pageSize = 50;

  dispatch(resetErrorDialog());

  try {
    const result: ICustomerAudit = await customerSupportApi.getCustomerAuditTrail(id, pageSize);
    while (result?.meta?.cursor) {
      const cursor = result?.meta?.cursor
      const response = await customerSupportApi.getCustomerAuditTrail(id, 10, cursor);
      result.data = result.data.concat(response.data);
      result.meta.cursor = response.meta.cursor;
    }
    return result;
  } catch (error) {
    dispatch(setErrorDialog({
      message: error.message,
      code: error.code || 'ERROR_CODE',
      title: error.title || 'Error Notification ',
    }));

    return rejectWithValue(error.message);
  }
},
  {
    condition: (refresh, { getState }) => {
      // execute the function if it's explicitly requested
      if (refresh) {
        return true;
      }

      const { customer } = getState();
      return customer.auditTrail.status === 'idle'; // execute the fetch function only when the status is idle
    },
  }
);

export const markCustomerForDeletion = createAsyncThunk<
  string,
  { id: string },
  { state: RootState }
>(
  'customer/deleteCustomerAccount',
  async (_, { dispatch, getState, rejectWithValue }) => {
    dispatch(resetErrorDialog());

    try {
      const { customer } = getState();
      const response = await customerSupportApi.markCustomerForDeletion(customer.id);

      if (!response) {
        return;
      }

      if (response === 'MAIL_SEND_ERROR') {
        throw new SliceError({
          code: 'MAIL_SEND_ERROR',
          title: 'Unable to send SUSPEND to Customer',
          message: 'The deletion email could not send to the customer.',
        }
        )
      } else {
        throw new SliceError({
          code: response,
          title: 'Unable to send SUSPEND to Customer',
          message: 'The Customer could not be SUSPENDED because of: ' + response,
        }
        )
      }
    } catch (error) {
      dispatch(setErrorDialog({
        message: error.message,
        code: error.code || 'ERROR_CODE',
        title: error.title || 'Error Notification ',
      }));

      return rejectWithValue(error.message);
    }
  }
);

export const unmarkCustomerForDeletion = createAsyncThunk<
  ICustomerData,
  { id: string },
  { state: RootState }
>(
  'customer/undeleteCustomerAccount',
  async (_, { dispatch, getState, rejectWithValue }) => {
    dispatch(resetErrorDialog());

    try {
      const { customer } = getState();
      const response = await customerSupportApi.unmarkCustomerForDeletion(customer.id);

      processStringifiedResponseObject(response);

    } catch (error) {
      dispatch(setErrorDialog({
        message: error.message,
        code: error.code || 'ERROR_CODE',
        title: error.title || 'Error Notification ',
      }));

      return rejectWithValue(error.message);
    }
  }
);

export const resetIdVerification = createAsyncThunk<
  ICustomerData,
  { id: string },
  { state: RootState }
>(
  'customer/resetIdVerification',
  async (_, { dispatch, getState, rejectWithValue }) => {
    dispatch(resetErrorDialog());

    try {
      const { customer } = getState();
      const response = await customerSupportApi.resetIdVerification(customer.id);
      processStringifiedResponseObject(response);

    } catch (error) {
      dispatch(setErrorDialog({
        message: error.message,
        code: error.code || 'ERROR_CODE',
        title: error.title || 'Error Notification ',
      }));
      return rejectWithValue(error.message);
    }
  }
);

function processStringifiedResponseObject(response: string) {
  let parsedObj;

  try {
    parsedObj = JSON.parse(response);
  } catch (error) {
    console.log(response)
  }

  if (parsedObj && parsedObj.errors?.length > 0) {
    const error = parsedObj.errors[0];
    throw new SliceError({
      message: error.message,
      code: error.code || 'ERROR_CODE',
      title: error.title || 'Error Notification ',
    })
  }
}

export const suspendCustomer = createAsyncThunk<
  string,
  { id: string },
  { state: RootState }
>(
  'customer/suspendCustomer',
  async (_, { dispatch, getState, rejectWithValue }) => {
    dispatch(resetErrorDialog());

    try {
      const { customer } = getState();
      const response = await customerSupportApi.suspendCustomer(customer.id);

      if (response !== '') {
        throw new SliceError({
          code: 'CUSTOMER_IS_IN_ACTIVE_TRIP',
          title: 'Unable to Suspend Customer',
          message: 'The customer can not be suspended, because the customer is in an active trip.'
        }
        )
      }
    } catch (error) {
      dispatch(setErrorDialog({
        message: error.message,
        code: error.code || 'ERROR_CODE',
        title: error.title || 'Error Notification ',
      }));
      return rejectWithValue(error.message);
    }
  }
);

export const unsuspendCustomer = createAsyncThunk<
  ICustomerData,
  { id: string },
  { state: RootState }
>(
  'customer/unsuspendCustomer',
  async (_, { dispatch, getState, rejectWithValue }) => {
    dispatch(resetErrorDialog());

    try {
      const { customer } = getState();
      const response = await customerSupportApi.unsuspendCustomer(customer.id);

      processStringifiedResponseObject(response)
    } catch (error) {
      dispatch(setErrorDialog({
        message: error.message,
        code: error.code || 'ERROR_CODE',
        title: error.title || 'Error Notification ',
      }));
      return rejectWithValue(error.message);
    }
  }
);

export const addCustomerComment = createAsyncThunk<IComment[],
  {
    customerId: string,
    summary: string,
    description: string
  }, { state: RootState }>(
  'vehicles/create',
  async ({customerId, summary, description} , { dispatch, getState, rejectWithValue }) => {
    try {
      await customerSupportApi.addCustomerComment(customerId, summary, description);
      return await customerSupportApi.getCustomerComments(customerId);
    } catch (error){
      dispatch(setErrorDialog({
        message: 'Could not add comment for this customer. Please check the logs for details.',
        code: error.code || 'ERROR_CODE',
        title: error.title || 'Could not add comment',
      }));
      return rejectWithValue(error.message);
    }
  }
);


export const customerSlice = createSlice({
  name: 'customer',
  initialState: initialCustomerState,
  reducers: {
    setSearchTerm(state, action) {
      if (!action.payload) {
        state.search.identifier = undefined;
        state.search.type = undefined;
        state.search.result = undefined;
      } else {
        const searchTerm = action.payload.toLowerCase();
        state.search.identifier = {
          value: searchTerm
        };
        state.search.type = /^[0-9A-Fa-f]{8}(-[0-9A-Fa-f]{4}){3}-[0-9A-Fa-f]{12}$/.test(searchTerm) ? 'ID' :
          /^[0-9]*$/.test(searchTerm) ? 'DRIVER' : 'EMAIL'
      }
    },
    resetSearchResult(state) {
      state.search.result = undefined
    },
    setBookingSearchId(state, action) {
      if (action.payload === undefined) {
        state.search.identifier = undefined;
        state.search.type = undefined;
      } else {
        if (!state.search.identifier) {
          state.search.identifier = { bookingId: action.payload };
        } else {
          state.search.identifier.bookingId = action.payload;
        }
      }
    },
    setBookingSearchUserInput(state, action) {
      if (!action.payload) {
        state.search.identifier = undefined;
        state.search.type = undefined;
      } else {
        if (!state.search.identifier) {
          state.search.identifier = { userInput: action.payload };
        } else {
          state.search.identifier.userInput = action.payload
        }
      }
    },
    setSearchType(state, action) {
      if (!action.payload) {
        state.search.identifier = undefined;
        state.search.type = undefined;
      } else {
        state.search.type = action.payload.type;
      }
    },
    resetCustomer(state) {
      const resetBookingData = {
        data: [],
        futurePresentationData: [],
        futureFilteredData: [],
        pastPresentationData: [],
        pastFilteredData: [],
        filter: {},
      };

      const resetTripData = {
        data: [],
        presentationData: [],
        filteredData: [],
        filter: {},
      };

      state.customer = undefined;
      state.id = undefined;
      state.auditTrail.data = [];
      state.auditTrail.filter.auditChangedBy.customer = false;
      state.auditTrail.filter.auditChangedBy.support = false;

      state.bookings = {
        ...resetBookingData,
        status: 'idle',
      }

      state.trips = {
        ...resetTripData,
        status: 'idle',
      }
    },
    setAuditTrailFilterCustomerValue(state, action) {
      state.auditTrail.filter.auditChangedBy.customer = action.payload;
      state.auditTrail.data = state.auditTrail.orgData.filter(item =>
        item.auditChangedBy.startsWith('ChangedByCustomer') === action.payload ||
        !item.auditChangedBy.startsWith('ChangedByCustomer') === state.auditTrail.filter.auditChangedBy.support)
    },
    setAuditTrailFilterSupportValue(state, action) {
      state.auditTrail.filter.auditChangedBy.support = action.payload;
      state.auditTrail.data = state.auditTrail.orgData.filter(item =>
        !item.auditChangedBy.startsWith('ChangedByCustomer') === action.payload ||
        item.auditChangedBy.startsWith('ChangedByCustomer') === state.auditTrail.filter.auditChangedBy.customer)
    },
    ...tripsReducers,
    ...bookingReducers
  },
  extraReducers(builder) {
    builder
      .addCase(fetchCustomerDataByIdentifier.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCustomerDataByIdentifier.fulfilled, (state, action) => {
        state.status = 'succeeded';

        if (Array.isArray(action.payload)) {
          state.search.result = action.payload
        } else {
          state.customer = action.payload;
          state.id = action.payload.id;
        }
      })
      .addCase(fetchCustomerDataByIdentifier.rejected, (state) => {
        state.status = 'failed';
        state.id = undefined;
      })
      .addCase(fetchCustomerDataById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCustomerDataById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.customer = state.customer = action.payload;
        state.id = action.payload.id;
      })
      .addCase(fetchCustomerDataById.rejected, (state) => {
        state.status = 'failed';
        state.id = undefined;
      })
      .addCase(markCustomerForDeletion.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(markCustomerForDeletion.fulfilled, (state) => {
        state.status = 'succeeded';
        state.customer = undefined;
        state.id = undefined;
      })
      .addCase(markCustomerForDeletion.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(unmarkCustomerForDeletion.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(unmarkCustomerForDeletion.pending, (state) => {
        state.status = 'loading';

      })
      .addCase(unmarkCustomerForDeletion.fulfilled, (state) => {
        state.status = 'succeeded';
        state.customer = undefined;
        state.id = undefined;
      })
      .addCase(resetIdVerification.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(resetIdVerification.fulfilled, (state) => {
        state.status = 'succeeded';
        state.customer = undefined;
        state.id = undefined;
      })
      .addCase(resetIdVerification.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(suspendCustomer.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(suspendCustomer.fulfilled, (state) => {
        state.status = 'succeeded';
        state.customer = undefined;
        state.id = undefined;
      })
      .addCase(suspendCustomer.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(unsuspendCustomer.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(unsuspendCustomer.fulfilled, (state) => {
        state.status = 'succeeded';
        state.customer = undefined;
        state.id = undefined;
      })
      .addCase(unsuspendCustomer.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(fetchCustomerAuditTrail.pending, (state) => {
        state.auditTrail.status = 'loading';
      })
      .addCase(fetchCustomerAuditTrail.fulfilled, (state, action) => {
        state.auditTrail.orgData = action.payload.data;
        state.auditTrail.data = action.payload.data;
        state.auditTrail.status = 'succeeded';
      })
      .addCase(fetchCustomerAuditTrail.rejected, (state) => {
        state.id = undefined;
        state.auditTrail.status = 'failed';
      })
      .addCase(addCustomerComment.pending, (state, action) => {
        state.status = 'loading';
      })
      .addCase(addCustomerComment.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.customer.comments = action.payload;
        console.log('Slice');
      })
      .addCase(addCustomerComment.rejected, (state, action) => {
        state.status = 'failed';
      })

    addTripsCases(builder);
    addBookingCases(builder);
  },
});

export default customerSlice.reducer;

export const {
  resetCustomer,
  resetSearchResult,
  setAuditTrailFilterCustomerValue,
  setAuditTrailFilterSupportValue,
  setBookingSearchId,
  setBookingSearchUserInput,
  setBookingsDateRageFilter,
  setBookingsPrePaymentState,
  setBookingsStatus,
  setSearchTerm,
  setSearchType,
  setTripServiceProvider,
  setTripsDateRageFilter,
  setTripsPaymentState
} = customerSlice.actions

export const {
  fetchCustomerTrips,
  selectTrips,
} = CustomerTrips;

export const selectEmail = () => (state: RootState) => {
  return state.customer.search.identifier.value;
};
export const selectCustomer = () => (state: RootState) => {
  return state.customer;
};

export const selectAuditTrail = () => (state: RootState) => {
  return state.customer.auditTrail;
};

export const selectBookings = () => (state: RootState) => {
  return state.customer.bookings;
};


export const selectId = () => (state: RootState) => {
  return state.customer.id;
};

